import { defineStore } from 'pinia'
import { ref } from 'vue'

/**
 * 表单数据结构
 */
interface FormData {
    data: any
    timestamp: number
}

/**
 * 被中断的路由信息
 */
interface InterruptedRoute {
    path: string
    query?: Record<string, any>
    timestamp: number
}

/**
 * 表单状态持久化 Store
 * 用于在用户登出/刷新页面后恢复编辑中的表单数据
 */
export const useFormStateStore = defineStore('formState', () => {
    // 存储各个页面的表单数据 (key: pageKey, value: 表单数据)
    const formDataMap = ref<Record<string, FormData>>({})
    
    // 存储被中断的页面路径（用于 code=10006 后恢复）
    const interruptedRoute = ref<InterruptedRoute | null>(null)
    
    // 存储对话框状态（记录哪些对话框是打开的）
    const dialogStates = ref<Record<string, boolean>>({})
    
    // 🔥 存储是否因为 code=10006 中断（用于判断是否需要恢复数据）
    const wasInterruptedByAuth = ref(false)
    
    /**
     * 保存表单数据
     * @param key 页面唯一标识（如 'activity_edit'）
     * @param data 表单数据
     */
    const saveFormData = (key: string, data: any) => {
        try {
            formDataMap.value[key] = {
                data: JSON.parse(JSON.stringify(data)),
                timestamp: Date.now()
            }
            console.log('✅ [FormState] 表单数据已保存:', key)
        } catch (error) {
            console.error('❌ [FormState] 保存表单数据失败:', error)
        }
    }
    
    /**
     * 获取表单数据（带过期检查）
     * @param key 页面唯一标识
     * @param maxAge 最大有效期（毫秒），默认30分钟
     * @returns 表单数据或 null
     */
    const getFormData = (key: string, maxAge: number = 30 * 60 * 1000): any => {
        const saved = formDataMap.value[key]
        if (!saved) {
            console.log('⚠️ [FormState] 未找到保存的数据:', key)
            return null
        }
        
        const timeDiff = Date.now() - saved.timestamp
        if (timeDiff > maxAge) {
            // 数据过期，自动清除
            delete formDataMap.value[key]
            console.warn('⚠️ [FormState] 数据已过期并清除:', key, `(${Math.floor(timeDiff / 1000 / 60)}分钟前)`)
            return null
        }
        
        console.log('✅ [FormState] 获取保存的数据:', key, `(${Math.floor(timeDiff / 1000)}秒前保存)`)
        return saved.data
    }
    
    /**
     * 清除表单数据
     * @param key 页面唯一标识
     */
    const clearFormData = (key: string) => {
        if (formDataMap.value[key]) {
            delete formDataMap.value[key]
            console.log('✅ [FormState] 已清除表单数据:', key)
        }
    }
    
    /**
     * 清除所有过期数据
     * @param maxAge 最大有效期（毫秒），默认30分钟
     */
    const cleanExpiredData = (maxAge: number = 30 * 60 * 1000) => {
        const now = Date.now()
        let cleanedCount = 0
        
        Object.keys(formDataMap.value).forEach(key => {
            if (now - formDataMap.value[key].timestamp > maxAge) {
                delete formDataMap.value[key]
                cleanedCount++
            }
        })
        
        if (cleanedCount > 0) {
            console.log(`✅ [FormState] 已清除 ${cleanedCount} 个过期数据`)
        }
    }
    
    /**
     * 设置被中断的路由（用于 code=10006 后恢复）
     * @param path 路由路径
     * @param query 路由查询参数（可选）
     */
    const setInterruptedRoute = (path: string, query?: Record<string, any>) => {
        interruptedRoute.value = {
            path,
            query,
            timestamp: Date.now()
        }
        console.log('✅ [FormState] 已保存被中断的路由:', path)
    }
    
    /**
     * 获取并清除被中断的路由
     * @param maxAge 最大有效期（毫秒），默认30分钟
     * @returns 路由信息或 null
     */
    const getAndClearInterruptedRoute = (maxAge: number = 30 * 60 * 1000): InterruptedRoute | null => {
        if (!interruptedRoute.value) {
            return null
        }
        
        const timeDiff = Date.now() - interruptedRoute.value.timestamp
        if (timeDiff > maxAge) {
            console.warn('⚠️ [FormState] 路由记录已过期')
            interruptedRoute.value = null
            return null
        }
        
        const route = { ...interruptedRoute.value }
        interruptedRoute.value = null
        console.log('✅ [FormState] 恢复被中断的路由:', route.path)
        return route
    }
    
    /**
     * 保存对话框状态
     * @param key 对话框唯一标识
     * @param isOpen 是否打开
     */
    const setDialogState = (key: string, isOpen: boolean) => {
        dialogStates.value[key] = isOpen
        console.log(`✅ [FormState] 对话框状态已保存: ${key} = ${isOpen}`)
    }
    
    /**
     * 获取对话框状态
     * @param key 对话框唯一标识
     */
    const getDialogState = (key: string): boolean => {
        return dialogStates.value[key] || false
    }
    
    /**
     * 🔥 设置中断标记（只在 code=10006 时调用）
     */
    const setInterruptedByAuth = () => {
        wasInterruptedByAuth.value = true
        console.log('🔥 [FormState] 已设置中断标记（code=10006）')
    }
    
    /**
     * 🔥 检查是否因为 code=10006 中断
     */
    const checkWasInterruptedByAuth = (): boolean => {
        return wasInterruptedByAuth.value
    }
    
    /**
     * 🔥 清除中断标记（恢复数据后或正常关闭时调用）
     */
    const clearInterruptedFlag = () => {
        wasInterruptedByAuth.value = false
        console.log('✅ [FormState] 已清除中断标记')
    }
    
    /**
     * 清除所有数据（用于退出登录）
     */
    const clearAll = () => {
        formDataMap.value = {}
        interruptedRoute.value = null
        dialogStates.value = {}
        wasInterruptedByAuth.value = false
        console.log('✅ [FormState] 已清除所有数据')
    }
    
    /**
     * 获取存储信息（用于调试）
     */
    const getStorageInfo = () => {
        return {
            formDataCount: Object.keys(formDataMap.value).length,
            formDataKeys: Object.keys(formDataMap.value),
            hasInterruptedRoute: !!interruptedRoute.value,
            dialogStatesCount: Object.keys(dialogStates.value).length,
            wasInterruptedByAuth: wasInterruptedByAuth.value
        }
    }
    
    return {
        // 状态
        formDataMap,
        interruptedRoute,
        dialogStates,
        wasInterruptedByAuth,
        
        // 表单数据方法
        saveFormData,
        getFormData,
        clearFormData,
        cleanExpiredData,
        
        // 路由方法
        setInterruptedRoute,
        getAndClearInterruptedRoute,
        
        // 对话框方法
        setDialogState,
        getDialogState,
        
        // 🔥 中断标记方法
        setInterruptedByAuth,
        checkWasInterruptedByAuth,
        clearInterruptedFlag,
        
        // 工具方法
        clearAll,
        getStorageInfo
    }
}, {
    persist: {
        key: 'fenlike-form-state',
        storage: localStorage,
        paths: ['formDataMap', 'interruptedRoute', 'dialogStates', 'wasInterruptedByAuth']
    }
})

